/** A single desk tag. Structurally a subset of the printed list's row. */
export interface NameSlipRow {
    studentName: string;
    classSection?: string | null;
    rollNumber?: string | null;
    fatherName?: string | null;
    motherName?: string | null;
}

export interface NameSlipsPrintData {
    schoolName?: string;
    className?: string | null;
    sectionName?: string | null;
    rows: NameSlipRow[];
}

/** Slip values are interpolated into markup, so they must be escaped. */
function esc(v: string | number | null | undefined): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const clean = (v: string | null | undefined): string | null => {
    const s = (v ?? '').trim();
    return s.length > 0 ? s : null;
};

/** Long names must not push a fixed-height tag out of shape, so the display size
 *  steps down as the name grows. The class label shares the row, so the steps trip
 *  earlier than the name's own width would suggest. */
function nameSizeClass(name: string): string {
    if (name.length > 22) return 'name--xs';
    if (name.length > 15) return 'name--sm';
    return '';
}

/** Builds the print document. Split out from `printNameSlips` so the markup can be
 *  rendered and inspected without a browser. */
export function buildNameSlipsHtml(data: NameSlipsPrintData): string {
    const slipsHtml = data.rows.map((r) => {
        const name = clean(r.studentName) ?? '—';
        const father = clean(r.fatherName);
        const mother = clean(r.motherName);
        const roll = clean(r.rollNumber);

        /* The parent block keeps its height even when a name is missing, so tags stay
           on a common baseline and the sheet cuts straight. */
        const parents = `
          <div class="parents">
            <div class="parent">
              <span class="parent__label">Father</span>
              <span class="parent__value">${father ? esc(father) : '<span class="muted">&mdash;</span>'}</span>
            </div>
            <div class="parent">
              <span class="parent__label">Mother</span>
              <span class="parent__value">${mother ? esc(mother) : '<span class="muted">&mdash;</span>'}</span>
            </div>
          </div>`;

        return `
      <div class="slip">
        <div class="slip__inner">
          <div class="slip__top">
            <span class="school">${esc(data.schoolName || 'Gurukul Academy')}</span>
            ${roll ? `<span class="roll"><span class="roll__label">Roll</span>${esc(roll)}</span>` : ''}
          </div>
          <div class="slip__body">
            <div class="slip__row">
              <span class="name ${nameSizeClass(name)}">${esc(name)}</span>
              <span class="class">${clean(r.classSection) ? esc(r.classSection!) : '<span class="muted">&mdash;</span>'}</span>
            </div>
          </div>
          ${parents}
        </div>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Name Slips</title>
<style>
  /* A4 portrait: 210mm x 297mm. With 8mm margins the sheet holds a 2 x 9 grid of
     97mm x 30mm tags — 18 to a page. Two columns (not three) because the full width
     is what keeps a long name on a single line at a readable size. */
  @page { size: A4 portrait; margin: 8mm; }

  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #0f172a;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    /* Kills the whitespace between inline-block tags. */
    font-size: 0;
  }

  /* Inline-block (not grid/flex) because block flow is what paginates predictably
     across printed pages. */
  .slip {
    display: inline-block;
    vertical-align: top;
    width: 50%;
    height: 30mm;
    padding: 0.8mm;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* The dashed rule is the cut guide. */
  .slip__inner {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 2mm 3mm 1.8mm;
    border: 1px dashed #94a3b8;
    border-radius: 1.5mm;
  }

  .slip__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    padding-bottom: 1.2mm;
    border-bottom: 1px solid #e2e8f0;
  }
  .school {
    font-size: 6.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #94a3b8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .roll {
    display: inline-flex;
    align-items: baseline;
    gap: 2px;
    flex-shrink: 0;
    padding: 0.5px 5px;
    border: 1px solid #cbd5e1;
    border-radius: 100px;
    font-size: 9.5px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: #0f172a;
  }
  .roll__label {
    font-size: 6px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
  }

  /* Name and class share one baseline: the name takes the free space, the class
     sits at the end of the row and never shrinks. The row is centred in whatever
     height is left over, so the leftover never pools under the name. */
  .slip__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 0;
  }
  .slip__row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 3mm;
  }
  .name {
    flex: 1;
    min-width: 0;
    font-size: 17px;
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
  }
  .name--sm { font-size: 14px; }
  .name--xs { font-size: 11.5px; }
  .class {
    flex-shrink: 0;
    font-size: 8.5px;
    font-weight: 600;
    white-space: nowrap;
    color: #475569;
  }

  .parents {
    display: flex;
    gap: 3mm;
    padding-top: 1.2mm;
    border-top: 1px solid #e2e8f0;
  }
  .parent { flex: 1; min-width: 0; }
  .parent__label {
    display: block;
    font-size: 5.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
  }
  .parent__value {
    display: block;
    font-size: 8px;
    font-weight: 600;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .muted { color: #cbd5e1; font-weight: 400; }
</style>
</head>
<body>
  ${slipsHtml}
<script>
  window.onload = function () { window.print(); };
<${'/'}script>
</body>
</html>`;

    return html;
}

export function printNameSlips(data: NameSlipsPrintData) {
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (w) {
        w.document.write(buildNameSlipsHtml(data));
        w.document.close();
    }
}
